import { Injectable } from '@angular/core';
import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';
import { authCodeFlowConfig } from 'src/app/utils/auth.config';
import { AuthService } from '../common/auth.service';
import { HealthAppService } from './health-app.service';
import { SnackbarService } from '../common/snackbar.service';
import {
  Constants,
  InsightCustomEvents,
  InsightEvents,
} from 'src/app/utils/constants';
import { Router } from '@angular/router';
import { Subject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  private readonly _onLogoutSubject$ = new Subject<any>();
  public onLogoutSubject$: Observable<any> =
    this._onLogoutSubject$.asObservable();

  constructor(
    private readonly auth: AuthService,
    private readonly oauthService: OAuthService,
    private readonly haService: HealthAppService,
    private readonly snackbar: SnackbarService,
    private readonly router: Router
  ) {}

  public async logout(isUserAuthorized: boolean = true) {
    try {
      if (isUserAuthorized && this.auth.isAuthenticated()) {
        let metrixPayload = this.auth.getUserLoginMetrics();
        let userId = this.auth.getUserId();
        if (metrixPayload && userId) {
          this.haService.sendInsights(
            InsightEvents.User_Sessions,
            { ...metrixPayload, userId: userId },
            InsightCustomEvents.User_OUT
          );
        }
      }
    } catch (error) {
      console.error('Error during logout metrics logging:', error);
    }

    this.triggerLogoutEvent();

    let issuer = this.getIssuers();
    if (issuer) {
      try {
        this.oauthService.configure(this.formAuthConfig(issuer, false));
        await this.oauthService.loadDiscoveryDocumentAndTryLogin();
        this.auth.clearUserData();
        this.haService.selectedState = null;
        this.haService.countySelected = null;
        this.oauthService.postLogoutRedirectUri = document.baseURI + `#/home`;
        let idToken = this.oauthService.getIdToken();
        console.log('ID Token:', idToken);
        if (idToken) {
          this.oauthService.logOut({
            postLogoutRedirectUri: this.oauthService.postLogoutRedirectUri,
            state: 'logout',
          });
        } else {
          this.initiateCodeFlow(issuer, true, true);
        }
      } catch (error) {
        console.error('Error during OAuth logout:', error);
        this.hardLogout();
      }
    } else {
      this.hardLogout();
    }
  }

  triggerLogoutEvent() {
    this._onLogoutSubject$.next(true);
  }

  hardLogout() {
    this.auth.clearUserData();
    this.haService.selectedState = null;
    this.haService.countySelected = null;
    this.router.navigate(['/home']);
  }

  initiateCodeFlow(
    tenant: any,
    continueLogin: boolean,
    hideLoginMsg: boolean = false
  ) {
    this.configureOIDC(
      this.formAuthConfig(tenant),
      continueLogin,
      hideLoginMsg
    );
  }

  initiateCodeFlowWithConfig(
    tenant: AuthConfig,
    continueLogin: boolean,
    hideLoginMsg: boolean = false
  ) {
    this.configureOIDC(tenant, continueLogin, hideLoginMsg);
  }

  async configureOIDC(
    authCodeFlowConfig: AuthConfig,
    continueLogin: boolean,
    hideLoginMsg: boolean = false
  ) {
    this.oauthService.configure(authCodeFlowConfig);
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService
      .loadDiscoveryDocumentAndTryLogin()
      .then((success: boolean) => {})
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (!hideLoginMsg) {
          this.snackbar.openSuccessBar(
            'Successfully verified, redirecting you to login page. Please wait..'
          );
        }

        if (continueLogin) {
          this.oauthService.initCodeFlow();
        }

        this.oauthService.events.subscribe((e) => {
          // tslint:disable-next-line:no-console
          console.info('oauth/oidc event', e);

          if (e.type == 'silently_refreshed') {
            this.auth.tenantAccessToken = this.oauthService.getAccessToken();
            let tokenPayload = this.auth.isAuthenticated();
            if (tokenPayload?.token && tokenPayload?.refreshToken) {
              this.auth
                .refreshToken(tokenPayload.token, tokenPayload.refreshToken)
                .subscribe((user: any) => {
                  if (user) {
                    this.auth.hasAgreedTerns = user;
                    localStorage.setItem(
                      Constants.TOKEN_STORE,
                      JSON.stringify(user)
                    );
                  }
                });
            }
          }
        });
      });

    // }
  }

  formAuthConfig(item: any, saveIssuer: boolean = true): any {
    let config: AuthConfig = authCodeFlowConfig;
    if (item?.issuerDetails) {
      config.issuer = `${item.issuerDetails.authority}`;
      config.tokenEndpoint = `${item.issuerDetails.tokenUrl}`;
      config.clientId = `${item.issuerDetails.clientId}`;
      config.scope = `offline_access ${item.issuerDetails.scope?.trim()}`;
      if (saveIssuer) {
        this.setIssuers(item);
      }
    }
    return config;
  }

  public setIssuers(issuerDetails: any) {
    localStorage.setItem(Constants.ISSUERS, JSON.stringify(issuerDetails));
  }

  public getIssuers(): any {
    const user = localStorage.getItem(Constants.ISSUERS);
    if (user) {
      let tokenPayload = JSON.parse(user);
      return tokenPayload; // will add interceptor to check the validity later.
    }
    return null;
  }

  updateTokens() {
    let token = this.oauthService.getIdToken();
    if (token) {
      this.auth.setTempAccessToken(token);
      this.haService.triggerOrgaizationChange('loading');
      this.auth.IDPLogin().subscribe({
        next: (payload: any) => {
          if (payload) {
            this.auth.hasLoggedIn = payload;
            this.auth.hasAgreedTerns = payload;
            this.auth.tenantAccessToken = this.oauthService.getAccessToken();
            this.auth.setTempAccessToken(null);

            this.haService.triggerOrgaizationChange('success');
          } else {
            this.haService.triggerOrgaizationChange();
          }
        },
        error: (error: any) => {
          this.snackbar.openInfoBar(
            'It seems your session has expired. Please login again.'
          );
          this.haService.triggerOrgaizationChange();
        },
        complete: () => {},
      });
    } else {
      this.snackbar.openInfoBar(
        'It seems your session has expired. Please login again.'
      );
      this.logout(true);
    }
  }
}
